/**
 * Exit Intent Trigger
 *
 * Opens popup when user moves mouse toward the top of the viewport,
 * indicating they may be about to leave the page.
 *
 * Smart preloading: preloads on first mouse move to avoid impacting Lighthouse.
 */

import type { Popup } from '../popup';
import { InteractionPreloadStrategy } from '../preload-strategies/interaction-strategy';
import { createLogger } from '../../shared/logger';
import { TriggerStateBroadcaster } from './trigger-state-broadcaster';

const logger = createLogger('ExitIntentTrigger');

export class ExitIntentTrigger {
	private popup: Popup;
	private sensitivity: number;
	private hasTriggered = false;
	private boundExitHandler: (event: MouseEvent) => void;
	private preloadStrategy: InteractionPreloadStrategy | null = null;
	private broadcaster: TriggerStateBroadcaster;

	constructor(popup: Popup, sensitivity: number) {
		logger.log('constructor - sensitivity:', sensitivity);
		this.popup = popup;
		this.sensitivity = sensitivity;
		this.boundExitHandler = this.handleExitIntent.bind(this);
		this.broadcaster = new TriggerStateBroadcaster(
			popup.getFeedId(),
			'exit_intent'
		);
	}

	/**
	 * Initialize the trigger
	 */
	public init(): void {
		logger.log('init() called');

		// Broadcast initial waiting state
		this.broadcaster.setState('waiting');

		// Preload on first mouse move (user interaction)
		this.preloadStrategy = new InteractionPreloadStrategy('mousemove');
		this.preloadStrategy.init(() => {
			logger.log('Interaction preload triggered');
			this.broadcaster.setState('preloading');
			this.popup.preload();
		});

		// Listen for exit intent
		document.addEventListener('mouseleave', this.boundExitHandler);
		document.addEventListener('mouseout', this.boundExitHandler);
	}

	/**
	 * Destroy the trigger
	 */
	public destroy(): void {
		logger.log('destroy() called');
		this.broadcaster.destroy();
		this.preloadStrategy?.destroy();
		document.removeEventListener('mouseleave', this.boundExitHandler);
		document.removeEventListener('mouseout', this.boundExitHandler);
	}

	/**
	 * Handle exit intent detection
	 */
	private handleExitIntent(event: MouseEvent): void {
		// Only trigger once per page load
		if (this.hasTriggered) {
			return;
		}

		// Check if mouse is leaving through the top of the viewport
		if (event.clientY <= this.sensitivity) {
			// For mouseout, check if we're actually leaving the document
			if (event.type === 'mouseout') {
				const target = event.relatedTarget as Node | null;
				if (target && document.contains(target)) {
					return;
				}
			}

			logger.log('Exit intent detected! Showing popup');
			this.hasTriggered = true;
			this.broadcaster.setState('triggered');
			this.popup.show();
		}
	}
}
